// The rows of the library of the DRAFT tab (R1888, mockup round 091 cards
// 01 and 02; ui-map-interface.md §12 "The DRAFT tab"). DRAFT · PRINTS and
// DRAFT · MASTERS draw each design in the specification row of BUILD
// (`buildrow.ts`): the hull mark and the name on line 1 (the name holds the
// hull name), the runs and the weight on line 2, the typed systems on
// line 3, which runs on under the cost column, the cost of one hull built
// to the design over its build time in the cost column, then ONE press in
// the press column and ⋯ at the right edge.
//
// - A master: PRINT ×N. Its ⋯ holds the REDRAFT row of each slot and
//   SCRAP.
// - A loose print (unspent, in no slot): INSTALL, armed over an occupied
//   slot (R696). Its ⋯ holds the price and SELL, and SCRAP.
// - The print in its slot: the dim word USED. A spent print: the dim
//   word SPENT. Its ⋯ holds SCRAP.
//
// SCRAP is armed (R696). The module is pure: it holds no field of the
// overlay.

import type { Blueprint } from '../../gen/proto/aquaphobia';
import { card as CS, overlay as S } from '../../gen/strings';
import { armFaces } from '../choice/choice';
import { esc } from '../esc';

/** The one press of a library row. */
type LibraryPress = 'print' | 'install' | 'inuse' | 'spent';

/**
 * The press of one design of the library. `installed` is the print that
 * stands in the slot of the design's hull class, or 0. A spent print may
 * still hold its slot until the work that paid for its last run is laid
 * down (R216, R217), and it reads SPENT.
 */
export function libraryPress(
  bp: Pick<Blueprint, 'id' | 'master' | 'runs'>,
  installed: number,
): LibraryPress {
  if (bp.master) return 'print';
  if (bp.runs <= 0) return 'spent';
  return installed === bp.id ? 'inuse' : 'install';
}

/** What the press column of a library row needs beside the press. */
interface LibraryPressIn {
  id: number;
  name: string;
  /** The word of PRINT ×N, with the runs of a standard print. */
  printWord: string;
  /** The slot of the hull class holds another print: INSTALL is armed. */
  overSlot: boolean;
  /** The first press of INSTALL armed it: the press shows INSTALL? (R2069). */
  installArmed: boolean;
  moreOpen: boolean;
}

/**
 * The press and ⋯ of a library row: the press stands in the press column
 * and ⋯ in the column at the right edge of the row (`withmore`), as the
 * ⋯ of ECONOMY and WAR does. A press that
 * does not act (USED, SPENT) is a dim word that keeps the width of a
 * press, as MAX and DONE do in BUILD.
 */
export function libraryPressHtml(p: LibraryPress, o: LibraryPressIn): string {
  let press: string;
  switch (p) {
    case 'print':
      press = `<button class="bp ready" data-act="print" data-id="${o.id}">`
        + `<span class="w">${esc(o.printWord)}</span></button>`;
      break;
    case 'install':
      press = `<button class="bp ready${o.installArmed ? ' armed' : ''}" data-act="install" data-id="${o.id}"`
        + (o.overSlot ? ` data-arm="1" title="${esc(S.print_install_title)}"` : '')
        + `><span class="w">${armFaces(S.btn_install, S.install_armed, o.installArmed)}</span></button>`;
      break;
    case 'inuse':
      press = `<span class="bp tag" title="${esc(S.print_installed)}"><span class="w">${S.lib_in_use}</span></span>`;
      break;
    default:
      press = `<span class="bp tag" title="${esc(S.print_spent)}"><span class="w">${S.lib_spent}</span></span>`;
  }
  const more = `<button class="rmore${o.moreOpen ? ' on' : ''}" data-act="libmore" data-id="${o.id}"`
    + ` title="${esc(S.lib_more_title({ name: o.name }))}" aria-expanded="${o.moreOpen}">${CS.more_mark}</button>`;
  return `<span class="lp">${press}${more}</span>`;
}
